// API client for Growtify Cockpit.
// In dev: vite proxy adds the ops token automatically.
// In prod (Cloudflare Pages): explicit cross-origin call to Worker w/ token.

const API_BASE = import.meta.env.VITE_API_BASE || "https://ops.growtify.app";
const API_TOKEN = import.meta.env.VITE_API_TOKEN || "ops-growtify-2026";

// Impersonation: when set in localStorage, every fetch appends impersonation
// params so Worker resolveEffectiveRole() applies the overlay. Cleared via
// useEffectiveRole().clearImpersonation().
const IMPERSONATE_ROLE_KEY = "growtify-cockpit-impersonate-role";
const IMPERSONATE_TENANT_KEY = "growtify-cockpit-impersonate-tenant";

function appendImpersonation(url: URL): URL {
  if (typeof window === "undefined") return url;
  const role = window.localStorage.getItem(IMPERSONATE_ROLE_KEY);
  const tenant = window.localStorage.getItem(IMPERSONATE_TENANT_KEY);
  if (role) url.searchParams.set("impersonate_role", role);
  if (tenant) url.searchParams.set("impersonate_tenant", tenant);
  return url;
}

// Replace direct url.toString() with this in every endpoint helper to ensure
// impersonation propagates uniformly. New code: prefer this for new endpoints.
function buildAuthedUrl(path: string, params?: Record<string, string | undefined>): string {
  const url = new URL(`${API_BASE}${path}`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v != null) url.searchParams.set(k, v);
    }
  }
  appendImpersonation(url);
  return url.toString();
}

export const RBAC = {
  IMPERSONATE_ROLE_KEY,
  IMPERSONATE_TENANT_KEY,
  buildAuthedUrl,
};

export interface DashboardKPIs {
  active_clients: number;
  active_contracts: number;
  pending_inputs: number;
  pending_approvals: number;
  open_requests: number;
  p0_open: number;
}

export interface ClientRow {
  slug: string;
  display_name: string;
  vertical: string;
  status: string;
  currency_native: string;
  client_type: string;
}

export interface ContractRow {
  id: string;
  tenant_id: string;
  monthly_rate_native: number;
  currency_native: string;
  status: string;
  contract_start: string;
}

export interface InputRow {
  id: string;
  tenant_id: string;
  requester_department: string;
  title: string;
  priority: string;
  tier: number;
  created_at: string;
  deadline: string | null;
}

export interface ApprovalRow {
  id: string;
  tenant_id: string;
  content_type: string;
  tier: number;
  title: string;
  department: string;
  status: string;
  created_at: string;
  due_date: string | null;
  preview_url: string | null;
  content_summary: string | null;
}

export interface RequestRow {
  id: string;
  tenant_id: string;
  requester_department: string;
  assignee_department: string;
  status: string;
  priority: string;
  tier: number;
  title: string;
  customer_summary: string;
  routing_route: string | null;
  approval_status: string | null;
  created_at: string;
  deadline: string | null;
}

export interface BriefRow {
  id: string;
  exec_role: string;
  kind: string;
  title: string;
  summary: string;
  health_indicator: string | null;
  health_score: number | null;
  produced_at: string;
}

export interface DashboardPayload {
  source: string;
  generated_at: string;
  latency_ms: number;
  tenant: string | null;
  kpis: DashboardKPIs;
  clients: ClientRow[];
  contracts: ContractRow[];
  pending_inputs: InputRow[];
  pending_approvals: ApprovalRow[];
  open_requests: RequestRow[];
  recent_briefs: BriefRow[];
  health_scores: Array<{
    tenant_id: string;
    current_score: number | null;
    last_score: number | null;
    trend: string | null;
    measured_at: string;
  }>;
}

export async function fetchDashboard(tenant?: string): Promise<DashboardPayload> {
  const url = new URL(`${API_BASE}/api/dashboard`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  if (tenant) url.searchParams.set("tenant", tenant);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`Dashboard fetch failed: ${res.status}`);
  return res.json();
}

export interface ActionResponse {
  ok: boolean;
  id: string;
  status?: string;
  at?: string;
  actor?: string;
  error?: string;
  details?: string[];
  rejection_feedback?: RejectionFeedback;
}

// Per cross-dept-routing.md — structured feedback mandatory on every reject.
export type RejectionCategory =
  | "scope_unclear"
  | "acceptance_criteria_missing"
  | "out_of_sla"
  | "brand_safety_risk"
  | "data_integrity_risk"
  | "budget_concern"
  | "strategic_conflict"
  | "technical_infeasible"
  | "duplicate_request"
  | "insufficient_evidence"
  | "compliance_risk"
  | "other";

export const REJECTION_CATEGORIES: { value: RejectionCategory; label: string }[] = [
  { value: "scope_unclear", label: "Scope unclear" },
  { value: "acceptance_criteria_missing", label: "Acceptance criteria missing" },
  { value: "out_of_sla", label: "Out of SLA" },
  { value: "brand_safety_risk", label: "Brand safety risk" },
  { value: "data_integrity_risk", label: "Data integrity risk" },
  { value: "budget_concern", label: "Budget concern" },
  { value: "strategic_conflict", label: "Strategic conflict" },
  { value: "technical_infeasible", label: "Technical infeasible" },
  { value: "duplicate_request", label: "Duplicate request" },
  { value: "insufficient_evidence", label: "Insufficient evidence" },
  { value: "compliance_risk", label: "Compliance risk" },
  { value: "other", label: "Other" },
];

export interface RejectionFeedback {
  rejected_at?: string;
  rejected_by?: string;
  rejector_role?: "peer_reviewer" | "ceo";
  reason_category: RejectionCategory;
  reason_detail: string;            // ≥30 chars
  improvement_suggestions: string[]; // non-empty
  can_resubmit?: boolean;
  resubmit_after?: string | null;
  related_documents?: string[];
  shared_learning?: string | null;
}

// ─── Comm primitives (threads + messages + notifications) ─────────────────

export interface Thread {
  id: string;
  tenant_id: string;
  subject_type: string;
  subject_id: string;
  visibility: "internal" | "shared";
  title: string | null;
  created_by_user_id: string;
  created_at: string;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
}

export interface ThreadMessage {
  id: string;
  thread_id: string;
  author_user_id: string;
  author_role: string;
  author_email?: string | null;
  author_display_name?: string | null;
  body_md: string;
  attachments: Array<{ kind: string; url: string; label: string }>;
  posted_at: string;
  edited_at: string | null;
}

export interface Notification {
  id: string;
  tenant_id: string;
  event_type: string;
  subject_type: string | null;
  subject_id: string | null;
  title: string;
  body_short: string | null;
  body_full_md: string | null;
  priority: "critical" | "high" | "normal" | "low";
  created_at: string;
  dashboard_seen_at: string | null;
  acknowledged_at: string | null;
  dismissed_at: string | null;
}

export async function fetchThreads(
  subject_type: string,
  subject_id: string
): Promise<{ ok: boolean; threads: Thread[] }> {
  const url = new URL(`${API_BASE}/api/threads`);
  url.searchParams.set("token", API_TOKEN);
  url.searchParams.set("subject_type", subject_type);
  url.searchParams.set("subject_id", subject_id);
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`threads ${res.status}`);
  return res.json();
}

export async function createThread(input: {
  tenant_id: string;
  subject_type: string;
  subject_id: string;
  title?: string;
  visibility?: "internal" | "shared";
}): Promise<{ ok: boolean; thread_id?: string; error?: string }> {
  const url = new URL(`${API_BASE}/api/threads`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return res.json();
}

export async function fetchMessages(
  thread_id: string
): Promise<{ ok: boolean; messages: ThreadMessage[] }> {
  const url = new URL(`${API_BASE}/api/threads/${encodeURIComponent(thread_id)}/messages`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`messages ${res.status}`);
  return res.json();
}

export async function postMessage(
  thread_id: string,
  body_md: string
): Promise<{ ok: boolean; message_id?: string; error?: string }> {
  const url = new URL(`${API_BASE}/api/threads/${encodeURIComponent(thread_id)}/messages`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ body_md }),
  });
  return res.json();
}

export async function fetchNotifications(opts: { unread_only?: boolean } = {}): Promise<{
  ok: boolean;
  notifications: Notification[];
  unread_count: number;
}> {
  const url = new URL(`${API_BASE}/api/notifications`);
  url.searchParams.set("token", API_TOKEN);
  if (opts.unread_only) url.searchParams.set("unread_only", "true");
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`notifications ${res.status}`);
  return res.json();
}

export async function notifAction(
  id: string,
  action: "seen" | "ack" | "dismiss"
): Promise<{ ok: boolean }> {
  const url = new URL(`${API_BASE}/api/notifications/${encodeURIComponent(id)}/${action}`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString(), { method: "POST" });
  return res.json();
}

// ─── Notification preferences ─────────────────────────────────────────────

export type NotifChannel = "bell" | "email" | "slack";

export interface NotifPreferenceOverride {
  event_type_pattern: string;
  channel: NotifChannel;
  enabled: 0 | 1;
  created_at: string;
  updated_at: string;
}

export interface NotifPreferencesResponse {
  ok: boolean;
  user_id: string;
  default_policy: {
    bell: { all: boolean; description: string };
    email: { critical: boolean; high: boolean; normal: boolean; low: boolean; description: string };
    slack: { critical: boolean; high: boolean; normal: boolean; low: boolean; description: string };
  };
  common_event_types: string[];
  overrides: NotifPreferenceOverride[];
}

export async function fetchNotifPreferences(): Promise<NotifPreferencesResponse> {
  const url = new URL(`${API_BASE}/api/me/notification-preferences`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`prefs ${res.status}`);
  return res.json();
}

export async function setNotifPreference(
  event_type_pattern: string,
  channel: NotifChannel,
  enabled: boolean
): Promise<{ ok: boolean }> {
  const url = new URL(`${API_BASE}/api/me/notification-preferences`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString(), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ event_type_pattern, channel, enabled }),
  });
  return res.json();
}

export async function deleteNotifPreference(
  event_type_pattern: string,
  channel: NotifChannel
): Promise<{ ok: boolean }> {
  const url = new URL(`${API_BASE}/api/me/notification-preferences`);
  url.searchParams.set("token", API_TOKEN);
  url.searchParams.set("event_type_pattern", event_type_pattern);
  url.searchParams.set("channel", channel);
  appendImpersonation(url);
  const res = await fetch(url.toString(), { method: "DELETE" });
  return res.json();
}

// ─── Client branding admin ────────────────────────────────────────────────

export interface ClientBrandingRow {
  tenant_id: string;
  display_name: string;
  logo_url: string | null;
  accent_color_primary: string;
  accent_color_secondary: string;
  vertical: string | null;
  /** From clients.status (joined). One of: active | active_pre-launch | onboarding | cancelled | null. */
  client_status: string | null;
  updated_at: string | null;
}

export async function fetchAllClientBrandings(): Promise<{
  ok: boolean;
  brandings: ClientBrandingRow[];
  error?: string;
}> {
  const url = new URL(`${API_BASE}/api/admin/client-brandings`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`brandings ${res.status}`);
  return res.json();
}

export async function uploadClientLogo(
  slug: string,
  file: File
): Promise<{ ok: boolean; key?: string; logo_url?: string; byte_length?: number; error?: string }> {
  const url = new URL(`${API_BASE}/api/admin/client-branding/${encodeURIComponent(slug)}/logo`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  return res.json();
}

// ─── Per-client deep-dive (cockpit /clients/:slug Overview tab) ────────────

export interface ClientSummaryBranding {
  tenant_id: string;
  display_name: string;
  logo_url: string | null;
  accent_color_primary: string;
  accent_color_secondary: string;
  powered_by_link_target: string | null;
  powered_by_text: string | null;
  vertical: string | null;
  currency_native: string | null;
  preferred_locale: string | null;
  client_status: string | null;
}

export interface ClientSummaryCounts {
  open_approvals: number;
  tier3_open: number;
  open_requests: number;
  p0_open: number;
  total_artifacts: number;
  open_threads: number;
  last_event_at: string | null;
}

export interface ClientSummaryApprovalRow {
  id: string;
  tier: number;
  title: string;
  content_type: string;
  department: string;
  status: string;
  due_date: string | null;
  created_at: string;
}

export interface ClientSummaryRequestRow {
  id: string;
  title: string;
  requester_dept: string;
  assignee_dept: string;
  status: string;
  priority: string;
  tier: number;
  created_at: string;
  deadline: string | null;
}

export interface ClientSummaryEventRow {
  id: string;
  event_type: string;
  actor_type: string;
  actor_id: string;
  subject_type: string | null;
  subject_id: string | null;
  occurred_at: string;
  source: string | null;
}

export interface ClientSummaryThreadRow {
  id: string;
  subject_type: string;
  subject_id: string;
  visibility: string;
  title: string | null;
  created_at: string;
  last_message_at: string | null;
}

export interface ClientSummaryResponse {
  ok: boolean;
  generated_at?: string;
  latency_ms?: number;
  branding?: ClientSummaryBranding;
  counts?: ClientSummaryCounts;
  open_approvals?: ClientSummaryApprovalRow[];
  open_requests?: ClientSummaryRequestRow[];
  recent_events?: ClientSummaryEventRow[];
  open_threads?: ClientSummaryThreadRow[];
  // Stubs filled by future phases:
  last_standup?: { date: string; summary: string; source_path: string } | null;
  this_week_priorities?: { priorities: string[] } | null;
  account_team?: { account_manager?: string; project_manager?: string; lead_strategist?: string } | null;
  campaigns?: Array<{ id: string; name: string; status: string; sprint_status?: string }>;
  performance?: unknown;
  error?: string;
}

export async function fetchClientSummary(slug: string): Promise<ClientSummaryResponse> {
  const url = new URL(`${API_BASE}/api/clients/${encodeURIComponent(slug)}/summary`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`client summary ${res.status}`);
  return res.json();
}

// Client per-tab full payloads ---

export interface ClientApprovalRow {
  id: string;
  tier: number;
  title: string;
  content_type: string;
  department: string;
  status: string;
  created_at: string;
  due_date: string | null;
  approved_at: string | null;
  approved_by: string | null;
  rejected_at: string | null;
  rejected_by: string | null;
  rejection_reason: string | null;
  rejection_feedback: string | null;
  produced_by: string | null;
  preview_url: string | null;
  live_url: string | null;
  publish_target: string | null;
  content_summary: string | null;
}

export type ClientApprovalsFilter = "all" | "open" | "approved" | "rejected";

export async function fetchClientApprovals(
  slug: string,
  opts: { filter?: ClientApprovalsFilter; limit?: number } = {}
): Promise<{
  ok: boolean;
  count: number;
  distribution: Record<string, number>;
  filter: string;
  approvals: ClientApprovalRow[];
}> {
  const url = new URL(`${API_BASE}/api/clients/${encodeURIComponent(slug)}/approvals`);
  url.searchParams.set("token", API_TOKEN);
  if (opts.filter) url.searchParams.set("filter", opts.filter);
  if (opts.limit) url.searchParams.set("limit", String(opts.limit));
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`approvals ${res.status}`);
  return res.json();
}

export interface ClientActivityRow {
  id: string;
  event_type: string;
  actor_type: string;
  actor_id: string;
  subject_type: string | null;
  subject_id: string | null;
  occurred_at: string;
  source: string | null;
  request_id: string | null;
}

export async function fetchClientActivity(
  slug: string,
  opts: { type?: string | null; limit?: number } = {}
): Promise<{
  ok: boolean;
  count: number;
  distribution: Record<string, number>;
  events: ClientActivityRow[];
}> {
  const url = new URL(`${API_BASE}/api/clients/${encodeURIComponent(slug)}/activity`);
  url.searchParams.set("token", API_TOKEN);
  if (opts.type) url.searchParams.set("type", opts.type);
  if (opts.limit) url.searchParams.set("limit", String(opts.limit));
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`activity ${res.status}`);
  return res.json();
}

export interface ClientThreadRow {
  id: string;
  subject_type: string;
  subject_id: string;
  visibility: string;
  title: string | null;
  created_by_user_id: string;
  created_at: string;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  message_count: number;
  last_message_at: string | null;
  last_message_preview: string | null;
}

export async function fetchClientThreads(
  slug: string,
  opts: { filter?: "open" | "resolved" | "all" } = {}
): Promise<{ ok: boolean; count: number; filter: string; threads: ClientThreadRow[] }> {
  const url = new URL(`${API_BASE}/api/clients/${encodeURIComponent(slug)}/threads`);
  url.searchParams.set("token", API_TOKEN);
  if (opts.filter) url.searchParams.set("filter", opts.filter);
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`threads ${res.status}`);
  return res.json();
}

export interface ClientArtifactRow {
  id: string;
  type: string;
  title: string;
  customer_summary: string | null;
  department: string;
  status: string;
  priority: string | null;
  owner: string | null;
  customer_visible: 0 | 1;
  created_at: string;
  updated_at: string;
  deadline: string | null;
  /** Phase 4e.3: customer education triple — populated when artifact follows
   *  artifact-schema.md §customer_education. Older artifacts may have nulls. */
  customer_education_what?: string | null;
  customer_education_why?: string | null;
  customer_education_expected?: string | null;
  /** Phase 4e.3: KPI tracking fields */
  kpi_metric?: string | null;
  kpi_baseline?: string | number | null;
  kpi_target_delta?: string | null;
  kpi_measurement_window?: string | null;
  /** Phase 4e.3: dept-specific rich payload (e.g., live_url, drive_url,
   *  preview_url for content artifacts; ad_account_id, campaign_id for ads;
   *  etc.) — JSON string from D1. */
  payload?: string | null;
  artifact_path?: string | null;
}

/** Helper to safely parse the artifact payload JSON. Returns {} on error. */
export function parseArtifactPayload(a: ClientArtifactRow): Record<string, any> {
  if (!a.payload) return {};
  if (typeof a.payload === "object") return a.payload as Record<string, any>;
  try {
    const parsed = JSON.parse(a.payload as string);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

export async function fetchClientArtifacts(
  slug: string,
  opts: { dept?: string; type?: string; status?: string; customerVisibleOnly?: boolean; limit?: number } = {}
): Promise<{
  ok: boolean;
  count: number;
  distribution_dept: Record<string, number>;
  distribution_status: Record<string, number>;
  artifacts: ClientArtifactRow[];
}> {
  const url = new URL(`${API_BASE}/api/clients/${encodeURIComponent(slug)}/artifacts`);
  url.searchParams.set("token", API_TOKEN);
  if (opts.dept) url.searchParams.set("dept", opts.dept);
  if (opts.type) url.searchParams.set("type", opts.type);
  if (opts.status) url.searchParams.set("status", opts.status);
  if (opts.customerVisibleOnly) url.searchParams.set("customer_visible", "true");
  if (opts.limit) url.searchParams.set("limit", String(opts.limit));
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`artifacts ${res.status}`);
  return res.json();
}

export async function fetchClientPerformance(slug: string): Promise<{
  ok: boolean;
  generated_at?: string;
  ga4?: unknown;
  gsc?: unknown;
  csuite_slice?: unknown;
  has_data?: boolean;
  error?: string;
}> {
  const url = new URL(`${API_BASE}/api/clients/${encodeURIComponent(slug)}/performance`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`performance ${res.status}`);
  return res.json();
}

// ─── Me (current user + effective role + permissions) ─────────────────────

export interface MeResponse {
  ok: boolean;
  user: { id: string; email: string; display_name: string } | null;
  role: string;
  actual_role: string;
  scope: string;
  is_impersonating: boolean;
  permissions: string[];
  internal_visible: boolean;
}

export async function fetchMe(): Promise<MeResponse> {
  const url = new URL(`${API_BASE}/api/me`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`me ${res.status}`);
  return res.json();
}

// ─── Client branding (per-tenant cobrand) ─────────────────────────────────

export interface ClientBranding {
  tenant_id: string;
  display_name: string;
  logo_url: string | null;
  accent_color_primary: string;
  accent_color_secondary: string;
  powered_by_link_target: string;
  powered_by_text: string;
  vertical: string | null;
  currency_native: string;
  preferred_locale: string;
  _is_fallback?: boolean;
}

export async function fetchClientBranding(
  slug: string
): Promise<{ ok: boolean; branding: ClientBranding }> {
  const url = new URL(`${API_BASE}/api/client-branding/${encodeURIComponent(slug)}`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`client-branding ${res.status}`);
  return res.json();
}

// ─── COO Overview state (Today's Plan, Critical Fires, Health Matrix, ...) ─

export interface TodayPlanItem {
  id?: string;       // work_items.id — present from Phase 1.7.6 onward, enables "Kapat" button
  rank: number;
  priority: string;
  client: string;
  title: string;
  status?: string;
  client_label?: string;
}

export interface StrategicPriority {
  rank: number;
  title: string;
  owner: string;
  deadline: string;
}

export interface BlockerForCEO {
  id: string;
  title: string;
  affects: string;
  aged_days: number;
  decision_due: string | null;
}

export interface ClientWeeklySlice {
  p0: string[];
  p1: string[];
  p2: string[];
  blocked: string[];
  active_req_count: number;
  notes: string;
}

export interface HealthMatrixRow {
  client: string;
  stage: string;
  last_state_delta: string;
  mgr_tmpl: string;
  w18_plan: string;
  active_req_count: string;
  health: string;
  note: string;
}

export interface InternalProjectStatus {
  phase: string | null;
  phase_status: string | null;
  next_gate_date: string | null;
  next_gate_name: string | null;
  session_summary: string | null;
  blockers: any[];
  adrs_open: any[];
  _source_file: string;
}

export interface PlanSummary {
  total_items: number | null;
  p0_count: number | null;
  p1_count: number | null;
  p2_count: number | null;
  blocked_count: number | null;
  tenants_touched: number | null;
}

export interface PlanItem {
  id: string;
  title: string;
  client: string;
  dept: string;
  priority: "P0" | "P1" | "P2" | string;
  owner: string;
  day_slot: string;
  estimated_effort?: string;
  dependencies?: string[];
  blocks?: string[];
  source?: string;
  source_ref?: string;
  action_required?: string;
}

export interface CadenceItem {
  day: string;
  time: string;
  owner: string;
  skill: string;
  status: string;
}

export interface WipDeptRollup {
  dept: string;
  session_id: string | null;
  generated_at: string | null;
  session_type: string | null;
  open_count: number;
  p0: number;
  p1: number;
  p2: number;
  blocked_count: number;
  completed_today_count: number;
  saturation_pct: number;
  remaining_hours_today: number;
  remaining_hours_this_week: number;
  outgoing_handoffs_count: number;
  carryover_count: number;
  ceo_input_pending_count: number;
  _source_file: string;
}

export interface WipPattern {
  dept: string;
  pattern: string;
}

export interface WipCEOPending {
  dept: string;
  question: string;
}

export interface WipHandoff {
  from_dept: string;
  to_dept: string;
  task_summary: string;
  rationale: string;
  deadline: string | null;
  req_id_if_filed: string;
}

export interface WipAggregate {
  depts: WipDeptRollup[];
  patterns: WipPattern[];
  ceo_input_pending: WipCEOPending[];
  outgoing_handoffs: WipHandoff[];
  saturating_depts: WipDeptRollup[];
  _total_reports: number;
  _aggregated_at: string;
}

export interface COOState {
  generated_at: string;
  sources: Record<string, string | null>;
  week_label: string | null;
  today_plan: TodayPlanItem[];
  strategic_priorities: StrategicPriority[];
  blockers_for_ceo: BlockerForCEO[];
  by_client: Record<string, ClientWeeklySlice>;
  health_matrix: HealthMatrixRow[];
  internal_projects: InternalProjectStatus[];
  plan_summary: PlanSummary;
  items: PlanItem[];
  cadence_items: CadenceItem[];
  wip_aggregate: WipAggregate;
}

export async function fetchCOOState(): Promise<{
  ok: boolean;
  generated_at: string;
  latency_ms: number;
  state: COOState | null;
}> {
  const url = new URL(`${API_BASE}/api/coo/state`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`coo/state ${res.status}`);
  return res.json();
}

// ─── CFO snapshot ──────────────────────────────────────────────────────────

export interface CFOTotals {
  vendor_monthly_usd: number;
  infra_monthly_usd: number;
  contractor_monthly_usd: number;
  vendor_count: number;
  vendor_unverified_count: number;
  contractor_count: number;
  infra_count: number;
}

export interface VendorRow {
  vendor_id: string;
  vendor_name: string;
  category: string;
  monthly_cost_usd_estimated: number;
  currency_native: string;
  owner_dept: string | null;
  status: string;
  verification_status: string | null;
  renewal_date: string | null;
}

export interface InfraRow {
  infra_id: string;
  name: string;
  category: string;
  monthly_cost_usd_estimated: number;
  owner_dept: string | null;
  vendor_ref: string | null;
  status: string;
  verification_status: string | null;
}

export interface ContractorRow {
  contractor_id: string;
  contractor_name: string;
  role: string | null;
  monthly_cost_usd_estimated: number;
  owner_dept: string | null;
  status: string;
}

export interface RevenueRow {
  revenue_id: string;
  client_id: string | null;
  client_name: string | null;
  event_type: string;
  recognition_month: string | null;
  amount_usd: number;
  currency_native: string;
  status: string;
  received_at: string | null;
  recurring: number;
}

export interface CFOSnapshot {
  ok: boolean;
  generated_at: string;
  latency_ms: number;
  totals: CFOTotals;
  vendors: VendorRow[];
  infra: InfraRow[];
  contractors: ContractorRow[];
  recent_revenue: RevenueRow[];
}

// ─── CFO finance-summary (runway + portfolio P&L from KV) ───────────────────

export type RunwayAlert = "healthy" | "weekly_note" | "ceo_escalation" | "critical" | string;

export interface RunwaySnapshot {
  as_of: string | null;
  currency_base: string;
  cash_position_usd: number | null;
  monthly_revenue_usd: number | null;
  monthly_cash_burn_usd: number | null;
  monthly_economic_burn_usd: number | null;
  net_cash_flow_usd: number | null;
  cash_runway_months: number | null;
  economic_runway_months: number | null;
  alert_level: RunwayAlert | null;
  alert_thresholds: Record<string, string> | null;
  burn_breakdown: Record<string, any> | null;
  _source_file: string;
  _published_at: string;
}

export interface PLSnapshot {
  month: string | null;
  currency_base: string;
  revenue_total_usd: number | null;
  revenue_by_entity_usd: Record<string, number>;
  cost_total_usd: number | null;
  cost_vendor_infra_contractor_usd: number | null;
  cost_paid_media_spend_usd: number | null;
  margin_total_usd: number | null;
  margin_pct: number | null;
  _source_file: string;
  _published_at: string;
}

export interface FinanceSummary {
  ok: boolean;
  generated_at: string;
  latency_ms: number;
  runway: RunwaySnapshot | null;
  pl: PLSnapshot | null;
  meta: { runway: string | null; pl: string | null; published_at: string } | null;
}

export async function fetchFinanceSummary(): Promise<FinanceSummary> {
  const url = new URL(`${API_BASE}/api/cfo/finance-summary`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`cfo/finance-summary ${res.status}`);
  return res.json();
}

export async function fetchCFOSnapshot(): Promise<CFOSnapshot> {
  const url = new URL(`${API_BASE}/api/cfo/snapshot`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`cfo/snapshot ${res.status}`);
  return res.json();
}

// ─── Audit log ─────────────────────────────────────────────────────────────

export interface AuditDecision {
  id: string;
  tenant_id: string;
  content_type: string;
  tier: number;
  title: string;
  department: string;
  status: "approved" | "rejected";
  approved_at: string | null;
  approved_by: string | null;
  approved_by_source: string | null;
  rejected_at: string | null;
  rejected_by: string | null;
  rejected_by_source: string | null;
  rejection_reason: string | null;
  rejection_feedback: RejectionFeedback | null;
  created_at: string;
  due_date: string | null;
}

export interface AuditEvent {
  id: string;
  tenant_id: string;
  actor_type: string;
  actor_id: string;
  event_type: string;
  subject_type: string | null;
  subject_id: string | null;
  occurred_at: string;
  source: string | null;
  request_id: string | null;
}

export async function fetchAuditDecision(id: string): Promise<{
  ok: boolean;
  decision?: AuditDecision;
  linked_routing?: AuditRoutingDecision | null;
  error?: string;
}> {
  const url = new URL(`${API_BASE}/api/audit/decision/${encodeURIComponent(id)}`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString());
  if (res.status === 404) {
    const body = await res.json().catch(() => ({}));
    return { ok: false, error: body.error || "decision_not_found" };
  }
  if (!res.ok) throw new Error(`audit/decision ${res.status}`);
  return res.json();
}

export async function fetchAuditDecisions(opts: {
  limit?: number;
  tenant?: string | null;
  status?: "approved" | "rejected" | null;
} = {}): Promise<{ ok: boolean; count: number; decisions: AuditDecision[] }> {
  const url = new URL(`${API_BASE}/api/audit/decisions`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  if (opts.limit) url.searchParams.set("limit", String(opts.limit));
  if (opts.tenant) url.searchParams.set("tenant", opts.tenant);
  if (opts.status) url.searchParams.set("status", opts.status);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`audit/decisions ${res.status}`);
  return res.json();
}

export type RoutingRoute = "auto" | "peer" | "cfo" | "cmo" | "cso" | "ceo" | string;

export interface AuditRoutingDecision {
  id: string;
  tenant_id: string;
  requester_department: string;
  assignee_department: string;
  status: string;
  priority: string;
  tier: number;
  title: string;
  routing_resolved_at: string | null;
  routing_route: RoutingRoute | null;
  routing_reasoning: string | null;
  routing_applied_rules: string[];
  routing_peer_reviewer: string | null;
  routing_override_by: string | null;
  approval_status: string | null;
  approval_tier: number | null;
  approval_id: string | null;
  approval_executable_at: string | null;
  rejection_feedback: RejectionFeedback | null;
  created_at: string;
}

export async function fetchAuditRouting(opts: {
  limit?: number;
  tenant?: string | null;
  route?: RoutingRoute | null;
} = {}): Promise<{
  ok: boolean;
  count: number;
  distribution: Record<string, number>;
  decisions: AuditRoutingDecision[];
}> {
  const url = new URL(`${API_BASE}/api/audit/routing`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  if (opts.limit) url.searchParams.set("limit", String(opts.limit));
  if (opts.tenant) url.searchParams.set("tenant", opts.tenant);
  if (opts.route) url.searchParams.set("route", opts.route);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`audit/routing ${res.status}`);
  return res.json();
}

export async function fetchAuditEvents(opts: {
  limit?: number;
  tenant?: string | null;
  type?: string | null;
} = {}): Promise<{
  ok: boolean;
  count: number;
  distribution: Record<string, number>;
  events: AuditEvent[];
}> {
  const url = new URL(`${API_BASE}/api/audit/events`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  if (opts.limit) url.searchParams.set("limit", String(opts.limit));
  if (opts.tenant) url.searchParams.set("tenant", opts.tenant);
  if (opts.type) url.searchParams.set("type", opts.type);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`audit/events ${res.status}`);
  return res.json();
}

export interface ImpersonationLogRow {
  id: string;
  actor_user_id: string;
  impersonated_role: string;
  impersonated_tenant_id: string | null;
  started_at: string;
}

export async function fetchAuditImpersonations(opts: {
  limit?: number;
  actor?: string | null;
} = {}): Promise<{
  ok: boolean;
  count: number;
  distribution: Record<string, number>;
  impersonations: ImpersonationLogRow[];
}> {
  const url = new URL(`${API_BASE}/api/audit/impersonations`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  if (opts.limit) url.searchParams.set("limit", String(opts.limit));
  if (opts.actor) url.searchParams.set("actor", opts.actor);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`audit/impersonations ${res.status}`);
  return res.json();
}

export async function approveApproval(
  id: string,
  approvedBy = "huseyin@growtify.app"
): Promise<ActionResponse> {
  const url = new URL(`${API_BASE}/api/approval/${encodeURIComponent(id)}/approve`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ approved_by: approvedBy }),
  });
  return res.json();
}

export interface SubmitCrossDeptRequestInput {
  title: string;
  description: string;
  area_hint?: string | null;
  priority?: "P0" | "P1" | "P2" | "normal" | "important" | "urgent";
}

export interface SubmitCrossDeptRequestResponse {
  ok: boolean;
  request_id: string;
  assigned_to: string;
  error?: string;
}

/**
 * POST /api/cross-dept/submit?tenant={slug}
 * Used by client-dashboard "Yeni Talep Aç" modal. Worker creates a
 * cross-dept request record + index entry. Returns the new request_id.
 */
export async function submitCrossDeptRequest(
  tenant: string,
  input: SubmitCrossDeptRequestInput
): Promise<SubmitCrossDeptRequestResponse> {
  const url = new URL(`${API_BASE}/api/cross-dept/submit`);
  url.searchParams.set("token", API_TOKEN);
  url.searchParams.set("tenant", tenant);
  appendImpersonation(url);
  const res = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const json = await res.json();
  if (!res.ok || json.ok === false) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json;
}

// ─── Cross-dept request close (cockpit "Kapat" button + auto-close pipeline) ──

// ─── Work-item universal close (Phase 1.5.A endpoint, exposed via "Kapat" cockpit button) ───
//
// POSTs /api/work-items/:id/state-change with to_status (default "closed") + reason.
// Backend atomically updates D1, casades children/keywords if requested, broadcasts SSE.
// Cockpit React Query invalidates within <500ms, row disappears from active lists.

export async function closeWorkItem(
  id: string,
  opts: {
    reason?: string;
    toStatus?: "closed" | "obsolete" | "cancelled";
    closureCategory?: "completed" | "obsolete" | "duplicate" | "superseded" | "cancelled" | "deferred";
    closureKeywords?: string[];
    cascadeChildren?: boolean;
    cascadeCrossLayerKeywords?: boolean;
  } = {}
): Promise<{
  ok: boolean;
  id?: string;
  from_status?: string;
  to_status?: string;
  cascaded_children?: number;
  keyword_cascaded_count?: number;
  new_version?: number;
  error?: string;
  current_status?: string;
}> {
  const url = new URL(`${API_BASE}/api/work-items/${encodeURIComponent(id)}/state-change`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const body: any = {
    to_status: opts.toStatus || "closed",
  };
  if (opts.reason) body.reason = opts.reason;
  if (opts.closureCategory) body.closure_category = opts.closureCategory;
  if (opts.closureKeywords?.length) body.closure_keywords = opts.closureKeywords;
  const cascade: any = {};
  if (opts.cascadeChildren) cascade.close_children = true;
  if (opts.cascadeCrossLayerKeywords) cascade.cross_layer_keywords = true;
  if (Object.keys(cascade).length) body.cascade = cascade;

  const res = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function closeCrossDeptRequest(
  id: string,
  opts: { reason?: string; closedBy?: string } = {}
): Promise<{ ok: boolean; id: string; status?: string; closed_at?: string; idempotent?: boolean; error?: string }> {
  const url = new URL(`${API_BASE}/api/cross-dept-requests/${encodeURIComponent(id)}/close`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      reason: opts.reason,
      closed_by: opts.closedBy || "huseyin@growtify.app",
    }),
  });
  return res.json();
}

export async function rejectApproval(
  id: string,
  feedback: RejectionFeedback,
  rejectedBy = "huseyin@growtify.app"
): Promise<ActionResponse> {
  const url = new URL(`${API_BASE}/api/approval/${encodeURIComponent(id)}/reject`);
  url.searchParams.set("token", API_TOKEN);
  appendImpersonation(url);
  const res = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      rejected_by: rejectedBy,
      rejection_feedback: feedback,
    }),
  });
  const json = await res.json();
  if (!res.ok || json.ok === false) {
    const errLabel = json.error || `HTTP ${res.status}`;
    const details = Array.isArray(json.details) ? `: ${json.details.join("; ")}` : "";
    throw new Error(`${errLabel}${details}`);
  }
  return json;
}
